import type { Amount, FinancialRate } from '@orus.eu/amount'
import type {
  FrMrpaxeActivityClass,
  FrMrpaxeDerogationAssetsValue,
  FrMrpaxeDerogationBrokenMaterial,
  FrMrpaxeDerogationCashAndValues,
  FrMrpaxeDerogationGeneralDeductible,
  FrMrpaxeDerogationOptionBdgValue,
  FrMrpaxeDerogationOptionPeValue,
  FrMrpaxeDerogationOutdoorFurniture,
  FrMrpaxeDerogationPreservedGoods,
  FrMrpaxeDerogationRealEstate,
  FrMrpaxeDerogationStolenAssetsValue,
  FrMrpaxePolicyItem,
  FrMrpaxePricingVersion,
  FrMrpaxeProductVersion,
  FrMrpaxeSegment,
  InsuranceOption,
} from '@orus.eu/product'
import type { FeesAndDiscountsRates, PartnerApplicationFeeOneTime } from '@orus.eu/quote'

import { terrorismTaxName } from '../../common/terrorism-tax.ts'
import type { InsuranceOutput, QuoteV3 } from '../../common/v3/index.ts'

import type { FrMrpaxeBillable, FrMrpaxePricedOption } from './fr-mrpaxe-billables.ts'

/** The limits and deductibles an underwriter changed, which the tariff prices. */
type FrMrpaxeQuoteDerogations = {
  generalDeductible: FrMrpaxeDerogationGeneralDeductible
  stolenAssetsValue: FrMrpaxeDerogationStolenAssetsValue
  cashAndValues: FrMrpaxeDerogationCashAndValues
  brokenMaterial: FrMrpaxeDerogationBrokenMaterial
  preservedGoods: FrMrpaxeDerogationPreservedGoods
  optionPeValue: FrMrpaxeDerogationOptionPeValue
  optionBdgValue: FrMrpaxeDerogationOptionBdgValue
  outdoorFurniture: FrMrpaxeDerogationOutdoorFurniture
  realEstate: FrMrpaxeDerogationRealEstate
  assetsValue: FrMrpaxeDerogationAssetsValue
}

export type FrMrpaxeV1QuoteInput = {
  segment: FrMrpaxeSegment
  activityClass: FrMrpaxeActivityClass
  /**
   * The classes of the secondary and tertiary activities the business carries on, none when it has neither. The back
   * office picks them itself. A list rather than two optional fields, because the store persists an `undefined` field
   * as `null`, and a quote rebuilt from its stored input must read the same activities.
   */
  additionalActivityClasses: readonly FrMrpaxeActivityClass[]
  optionalityByPolicyItem: Record<FrMrpaxePolicyItem, InsuranceOption>
  premiumOverchargeRates: {
    liability: FinancialRate
    damage: FinancialRate
    fireBusinessInterruptionAndGoodwill: FinancialRate
  }
  /** The underwriter waived the 2025 majoration. */
  hasDerogationMajoration: boolean
  feesAndDiscountsRates: FeesAndDiscountsRates | undefined | null
  occupationStatus: 'owner' | 'tenant'
  assetsValue: Amount
  /** In square meters. */
  surface: number
  estimatedRevenue: Amount
  claimsInLastThreeYears: '0' | '1' | '2'
  isNewlyCreatedCompany: boolean
  activityAddress: { postCode: string; city: string }
  derogations: FrMrpaxeQuoteDerogations
}

const FrMrpaxeQuoteTaxes = [terrorismTaxName] as const
type FrMrpaxeQuoteTaxes = (typeof FrMrpaxeQuoteTaxes)[number]

export type FrMrpaxeQuoteInsuranceEntry = FrMrpaxeBillable | PartnerApplicationFeeOneTime | FrMrpaxeQuoteTaxes

export type FrMrpaxeQuote = QuoteV3<
  1,
  {
    1: {
      productModelVersion: FrMrpaxeProductVersion
      input: FrMrpaxeV1QuoteInput
      insuranceEntries: FrMrpaxeQuoteInsuranceEntry
      pricingVersion: FrMrpaxePricingVersion
    }
  }
>

/** The theft protection level the RETA agreed terms require, from the least to the most demanding. */
export const frMrpaxeProtectionLevels = ['1', '2', '3', '4'] as const
export type FrMrpaxeProtectionLevel = (typeof frMrpaxeProtectionLevels)[number]

export type BuildFrMrpaxeQuoteFn = (params: {
  input: FrMrpaxeV1QuoteInput
  pricingVersion: FrMrpaxePricingVersion
  productVersion: FrMrpaxeProductVersion
}) => FrMrpaxeQuote

export type GetFrMrpaxeOptionOutputsFn = (
  billables: FrMrpaxeQuote['yearly']['billables'],
  option: FrMrpaxePricedOption,
) => readonly InsuranceOutput[]
